import { useState } from 'react';
import { add, Charge, ChargeSchema } from '@/api/fee/charge';
import { Button } from '@/components/ui/button';
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectLabel,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
} from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@/components/ui/tooltip';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CirclePlus } from 'lucide-react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'sonner';

export function AddCharge() {
	const transSummaryFields: Array<string> = [
		'CPICAssessment',
		'EMPAFee',
		'FarmBureauFee',
		'FHCFAssessment',
		'FilingFee',
		'InspectionFee',
		'PolicyFee',
		'ProviderFee',
		'SLTax',
		'StampingFee',
		'StateFee1',
	];

	const [open, setOpen] = useState(false);
	const queryClient = useQueryClient();

	const { mutate } = useMutation({
		mutationFn: (newCharge: Charge) => add(newCharge),
		onSuccess: () => {
			toast.success(`Success! Charge has been successfully added.`, {
				dismissible: true,
			});
			setOpen(false);
			queryClient.invalidateQueries({
				queryKey: ['charges'],
			});
			reset();
		},
		onError: () => {
			toast.error(`Error! Charge could not be added.`, {
				dismissible: true,
			});
		},
	});

	const form = useForm<Charge>({
		resolver: zodResolver(ChargeSchema),
		mode: 'all',
		defaultValues: {
			isTax: false,
			transSummaryField: '',
		},
	});

	const { reset } = form;
	const onSubmit: SubmitHandler<Charge> = (values: Charge) => mutate(values);

	return (
		<div>
			<TooltipProvider>
				<Tooltip>
					<TooltipTrigger asChild>
						<Button className="m-auto" onClick={() => setOpen(true)}>
							<CirclePlus />
							Add New Charge
						</Button>
					</TooltipTrigger>
					<TooltipContent>
						<p>Add a new charge</p>
					</TooltipContent>
				</Tooltip>
			</TooltipProvider>

			<Sheet open={open} onOpenChange={(open) => setOpen(open)}>
				<SheetContent>
					<SheetHeader>
						<SheetTitle>Add a New Charge</SheetTitle>
						<SheetDescription></SheetDescription>
					</SheetHeader>
					<Form {...form}>
						<form onSubmit={form.handleSubmit(onSubmit)}>
							<div className="space-y-4">
								<FormField
									control={form.control}
									name="chargeName"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Charge Name</FormLabel>
											<FormControl>
												<Input {...field} value={field.value ?? ''} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="transSummaryField"
									render={({ field }) => (
										<FormItem>
											<FormLabel>TransSummary Field</FormLabel>
											<FormControl>
												<Select
													value={field.value ?? 'none'}
													onValueChange={(val) =>
														field.onChange(val === 'none' ? null : val)
													}
												>
													<SelectTrigger>
														<SelectValue placeholder="Select a field..." />
													</SelectTrigger>
													<SelectContent>
														<SelectGroup>
															<SelectLabel>TransSummary Fields</SelectLabel>
															{transSummaryFields!.map((field) => (
																<SelectItem key={field} value={field}>
																	{field}
																</SelectItem>
															))}
														</SelectGroup>
													</SelectContent>
												</Select>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="isTax"
									render={({ field }) => (
										<FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm">
											<div className="space-y-0.5">
												<FormLabel>Tax</FormLabel>
											</div>
											<FormControl>
												<Switch checked={!!field.value} onCheckedChange={field.onChange} />
											</FormControl>
										</FormItem>
									)}
								/>
							</div>

							<div className="mt-9 flex justify-end">
								<Button
									type="submit"
									disabled={!form.formState.isValid || form.formState.isSubmitting}
								>
									{form.formState.isSubmitting ? 'Adding...' : 'Add'}
								</Button>
							</div>
						</form>
					</Form>
				</SheetContent>
			</Sheet>
		</div>
	);
}
